/**
 * SpotifyProfilePage
 *
 * Route: /spotify-profile
 *
 * Minimal end-to-end test UI for the Spotify OAuth PKCE flow:
 *
 *  ─ Not authenticated → "Connect with Spotify" button → calls login()
 *  ─ Authenticated     → GET https://api.spotify.com/v1/me, renders the result
 *  ─ Logout button     → clears tokens and resets the UI
 *
 * The Authorization header is injected automatically by SpotifyAuthInterceptor,
 * so this page just calls HttpClient.get() with no manual token handling.
 *
 * The raw JSON response is displayed below the parsed fields so you can
 * verify the full payload during development.
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SpotifyAuthService } from '../../services/spotify-auth.service';

@Component({
  selector: 'app-spotify-profile',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, JsonPipe],
  templateUrl: './spotify-profile.page.html',
  styleUrls: ['./spotify-profile.page.scss'],
})
export class SpotifyProfilePage implements OnInit {
  isAuthenticated = false;
  loading         = false;
  error           = '';
  profile: any    = null;
  copied          = false;

  constructor(
    private spotifyAuth: SpotifyAuthService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.spotifyAuth.isAuthenticated();
    if (this.isAuthenticated) {
      this.fetchProfile();
    }
  }

  /** Redirects to Spotify to begin the OAuth PKCE flow. */
  login(): void {
    this.spotifyAuth.login();
  }

  /** Returns the raw access token string for display / debugging. */
  get accessToken(): string {
    return this.spotifyAuth.getStoredAccessToken() ?? '';
  }

  /** Copies the access token to the clipboard. */
  async copyToken(): Promise<void> {
    await navigator.clipboard.writeText(this.accessToken);
    this.copied = true;
    setTimeout(() => this.copied = false, 2000);
  }

  /** Clears stored Spotify tokens and resets the UI to the unauthenticated state. */
  logout(): void {
    this.spotifyAuth.logout();
    this.isAuthenticated = false;
    this.profile         = null;
    this.error           = '';
  }

  /**
   * Calls GET /v1/me.  SpotifyAuthInterceptor attaches Bearer token automatically.
   * Exposed as a public method so the template "Refresh" button can call it directly.
   */
  async fetchProfile(): Promise<void> {
    this.loading = true;
    this.error   = '';
    try {
      this.profile = await firstValueFrom(
        this.http.get<any>('https://api.spotify.com/v1/me')
      );
    } catch (err: any) {
      // Spotify wraps errors as { error: { status, message } }
      this.error =
        err?.error?.error?.message ??
        err?.message ??
        'Failed to fetch profile from Spotify.';
    } finally {
      this.loading = false;
    }
  }
}
